import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  doc,
  onSnapshot,
} from 'firebase/firestore';

import { useParams } from 'react-router-dom';

import { db } from '../firebase';
import { getBingoPattern, getPatternLabel } from '../data/bingoPatterns';

import './BingoDisplay.css';
import './BingoDisplayThemes.css';

const DISPLAY_MODES = ['live', 'upNext', 'intermission', 'announcement', 'standby'];
const BINGO_LETTERS = ['B', 'I', 'N', 'G', 'O'];

function deserializePattern(value) {
  if (Array.isArray(value) && value.length === 5 && value.every(Array.isArray)) {
    return value.map((row) => row.map(Boolean));
  }

  if (!Array.isArray(value) || value.length !== 25) {
    return null;
  }

  return Array.from({ length: 5 }, (_, row) =>
    value.slice(row * 5, row * 5 + 5).map(Boolean)
  );
}

function getPresentationPattern(patternId, customPattern) {
  if (patternId === 'custom') {
    return deserializePattern(customPattern);
  }

  return getBingoPattern(patternId)?.masks?.[0] || null;
}

function PatternPreview({ pattern }) {
  if (!pattern) return null;

  return (
    <div className="bingo-display-pattern">
      <div className="bingo-display-pattern-row">
        {BINGO_LETTERS.map((letter) => (
          <div className="bingo-display-pattern-cell bingo-display-pattern-letter" key={letter}>
            {letter}
          </div>
        ))}
      </div>
      {pattern.map((row, rowIndex) => (
        <div className="bingo-display-pattern-row" key={rowIndex}>
          {row.map((active, columnIndex) => {
            const free = rowIndex === 2 && columnIndex === 2;
            const highlighted = free || active;

            return (
              <div
                key={columnIndex}
                className={[
                  'bingo-display-pattern-cell',
                  highlighted ? 'active' : '',
                  free ? 'free' : '',
                ].filter(Boolean).join(' ')}
              >
                {free ? 'FREE' : highlighted ? 'X' : ''}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function getBingoLetter(number) {
  const value = Number(number);

  if (!Number.isInteger(value) || value < 1 || value > 75) {
    return null;
  }

  if (value <= 15) return 'B';
  if (value <= 30) return 'I';
  if (value <= 45) return 'N';
  if (value <= 60) return 'G';
  return 'O';
}

function normalizeBingoBall(ball) {
  let number = null;

  if (typeof ball === 'number') {
    number = ball;
  } else if (typeof ball === 'string') {
    const match = ball.trim().toUpperCase().match(/^(?:[BINGO]\s*)?(\d{1,2})$/);
    number = match ? Number(match[1]) : null;
  } else if (ball && typeof ball === 'object') {
    number = Number(ball.number);
  }

  const letter = getBingoLetter(number);

  return letter
    ? { letter, number: Number(number) }
    : null;
}

function BingoBall({ ball, size = 'medium' }) {
  const normalized = normalizeBingoBall(ball);
  const sizeClass = ['small', 'medium', 'large'].includes(size)
    ? size
    : 'medium';

  if (!normalized) {
    return (
      <div
        className={`live-bingo-ball live-bingo-ball-${sizeClass} live-bingo-ball-empty`}
        aria-label="No bingo ball"
      >
        <span className="live-bingo-ball-number">—</span>
      </div>
    );
  }

  return (
    <div
      className={`live-bingo-ball live-bingo-ball-${sizeClass} live-bingo-ball-${normalized.letter.toLowerCase()}`}
      aria-label={`${normalized.letter} ${normalized.number}`}
    >
      <span className="live-bingo-ball-letter">{normalized.letter}</span>
      <span className="live-bingo-ball-number">{normalized.number}</span>
    </div>
  );
}

function getAutoCallTimestamp(value) {
  if (Number.isFinite(Number(value))) return Number(value);
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (Number.isFinite(value?.seconds)) return value.seconds * 1000;
  return null;
}

function AudienceAutoCallCountdown({ enabled, nextCallAt }) {
  const deadline = getAutoCallTimestamp(nextCallAt);
  const [remaining, setRemaining] = useState(() => (
    enabled && deadline
      ? Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
      : null
  ));

  useEffect(() => {
    if (!enabled || !deadline) {
      setRemaining(null);
      return undefined;
    }

    const updateRemaining = () => setRemaining(
      Math.max(0, Math.ceil((deadline - Date.now()) / 1000))
    );
    updateRemaining();
    const intervalId = setInterval(updateRemaining, 250);
    return () => clearInterval(intervalId);
  }, [deadline, enabled]);

  if (remaining === null) return null;

  return (
    <div className={`bingo-display-auto-call-timer ${remaining <= 3 ? 'ending' : ''}`} role="timer" aria-label={`Next automatic ball in ${remaining} seconds`}>
      <span>NEXT BALL IN</span>
      <strong>00:{String(remaining).padStart(2, '0')}</strong>
    </div>
  );
}

function BingoDisplay() {
  const { eventId } = useParams();

  const [session, setSession] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState('');

  const [
    calledBallOverlay,
    setCalledBallOverlay,
  ] = useState(null);

  const [
    showWinnerOverlay,
    setShowWinnerOverlay,
  ] = useState(false);

  const previousNumbersRef =
    useRef(null);

  const overlayTimeoutRef =
    useRef(null);

  const winnerTimeoutRef =
    useRef(null);

  const previousWinnerRef =
    useRef(false);

  const displayMode = DISPLAY_MODES.includes(session?.displayControl?.mode)
    ? session.displayControl.mode
    : 'live';

  useEffect(() => {
    if (!eventId) {
      setError(
        'No bingo event was selected.'
      );

      setLoading(false);

      return;
    }

    const ref = doc(
      db,
      'bingoMachineEvents',
      eventId
    );

    const unsubscribe =
      onSnapshot(
        ref,

        snapshot => {
          if (!snapshot.exists()) {
            setSession(null);

            setError(
              'This bingo event could not be found.'
            );

            setLoading(false);

            return;
          }

          setSession({
            id: snapshot.id,
            ...snapshot.data(),
          });

          setError('');
          setLoading(false);
        },

        err => {
          console.error(
            'Flashboard sync error:',
            err
          );

          setError(
            'Unable to connect to the bingo event.'
          );

          setLoading(false);
        }
      );

    return () =>
      unsubscribe();
  }, [eventId]);

  /*
   * Detect newly-called balls from the
   * live Firestore state.
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    const generatedNumbers =
      Array.isArray(
        session.generatedNumbers
      )
        ? session.generatedNumbers
        : [];

    if (
      previousNumbersRef.current ===
      null
    ) {
      previousNumbersRef.current =
        generatedNumbers;

      return;
    }

    if (displayMode !== 'live') {
      setCalledBallOverlay(null);

      if (overlayTimeoutRef.current) {
        clearTimeout(overlayTimeoutRef.current);
        overlayTimeoutRef.current = null;
      }
    }

    const previous =
      previousNumbersRef.current;

    if (
      generatedNumbers.length >
      previous.length
    ) {
      const previousSet =
        new Set(
          previous.map(
            ball => normalizeBingoBall(ball)?.number
          )
        );

      const newest =
        generatedNumbers.find(
          ball =>
            !previousSet.has(
              normalizeBingoBall(ball)?.number
            )
        ) ||
        generatedNumbers[
          generatedNumbers.length -
          1
        ];

      const normalizedNewest = normalizeBingoBall(newest);

      if (normalizedNewest && displayMode === 'live') {
        setCalledBallOverlay(
          normalizedNewest
        );

        if (
          overlayTimeoutRef.current
        ) {
          clearTimeout(
            overlayTimeoutRef.current
          );
        }

        overlayTimeoutRef.current =
          setTimeout(() => {
            setCalledBallOverlay(
              null
            );

            overlayTimeoutRef.current =
              null;
          }, 5000);
      }
    }

    previousNumbersRef.current =
      generatedNumbers;
}, [displayMode, session]);

  /*
   * React to operator's Call Winner.
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    const winnerActive = Boolean(session.showWinner);

    if (displayMode !== 'live') {
      setShowWinnerOverlay(false);

      if (winnerTimeoutRef.current) {
        clearTimeout(winnerTimeoutRef.current);
        winnerTimeoutRef.current = null;
      }
    } else if (winnerActive && !previousWinnerRef.current) {
      setShowWinnerOverlay(true);

      if (
        winnerTimeoutRef.current
      ) {
        clearTimeout(
          winnerTimeoutRef.current
        );
      }

      winnerTimeoutRef.current =
        setTimeout(() => {
          setShowWinnerOverlay(false);
        }, 6000);
    } else if (!winnerActive) {
      setShowWinnerOverlay(false);
    }

    previousWinnerRef.current = winnerActive;
  }, [displayMode, session]);
  
  useEffect(() => {
    return () => {
      if (
        overlayTimeoutRef.current
      ) {
        clearTimeout(
          overlayTimeoutRef.current
        );
      }

      if (
        winnerTimeoutRef.current
      ) {
        clearTimeout(
          winnerTimeoutRef.current
        );
      }
    };
  }, []);

  const calledNumbers =
    useMemo(() => {
      const list =
        Array.isArray(
          session?.generatedNumbers
        )
          ? session.generatedNumbers
          : [];

      return new Set(
        list
          .map((ball) => normalizeBingoBall(ball)?.number)
          .filter(Boolean)
      );
    }, [session?.generatedNumbers]);

  const activePattern = getPresentationPattern(
    session?.selectedPattern,
    session?.customPattern
  );
  const schedule = Array.isArray(session?.gameSchedule)
    ? session.gameSchedule
    : [];
  const savedActiveIndex = Number.isInteger(session?.activeGameIndex)
    ? session.activeGameIndex
    : schedule.findIndex((game) => game?.status === 'active');
  const isUpcoming = (game) => {
    const status = game?.status || (game?.completed ? 'completed' : 'scheduled');
    return ['scheduled', 'paused'].includes(status);
  };
  const nextScheduledGame = (
    savedActiveIndex >= 0
      ? schedule.slice(savedActiveIndex + 1).find(isUpcoming)
      : schedule.find((game) => (game?.status || 'scheduled') === 'scheduled')
  ) || schedule.find(isUpcoming) || null;
  const nextScheduledGameIndex = nextScheduledGame
    ? schedule.indexOf(nextScheduledGame)
    : -1;
  const nextScheduledPattern = getPresentationPattern(
    nextScheduledGame?.pattern,
    nextScheduledGame?.customPattern
  );

  if (loading) {
    return (
      <div className="bingo-display-screen">
        <div className="bingo-display-message">
          Connecting to flashboard...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bingo-display-screen">
        <div className="bingo-display-message bingo-display-error">
          {error}
        </div>
      </div>
    );
  }

  if (!session) {
    return null;
  }

  const recentCalledBalls =
    Array.isArray(session.generatedNumbers)
      ? session.generatedNumbers.slice(-5).reverse()
      : [];

  const theme =
    session.theme ||
    'standard';

  if (displayMode !== 'live') {
    const displayControl = session.displayControl || {};
    const intermissionMessage = displayControl.intermissionMessage?.trim()
      || 'Bingo will resume shortly.';
    const announcementTitle = displayControl.announcementTitle?.trim();
    const announcementMessage = displayControl.announcementMessage?.trim();

    return (
      <div className={`bingo-display-screen theme-${theme} bingo-display-mode-screen bingo-display-${displayMode}`}>
        <main className="bingo-display-mode-content">
          {displayMode === 'upNext' && (
            <>
              <span className="bingo-display-mode-eyebrow">UP NEXT</span>
              {nextScheduledGame ? (
                <div className="bingo-display-up-next-layout">
                  <div className="bingo-display-up-next-details">
                    <span className="bingo-display-up-next-game">
                      Game {nextScheduledGame.gameNumber || nextScheduledGameIndex + 1}
                    </span>
                    {nextScheduledGame.name && (
                      <h1 className="bingo-display-mode-title">{nextScheduledGame.name}</h1>
                    )}
                    <h2>{getPatternLabel(nextScheduledGame.pattern)}</h2>
                    {nextScheduledGame.prizeAmount && (
                      <strong className="bingo-display-up-next-prize">
                        ${nextScheduledGame.prizeAmount} Prize
                      </strong>
                    )}
                  </div>
                  {nextScheduledPattern && (
                    <div className="bingo-display-up-next-pattern">
                      <PatternPreview pattern={nextScheduledPattern} />
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <h1 className="bingo-display-mode-title">Up Next</h1>
                  <p className="bingo-display-mode-message">
                    No upcoming game is currently scheduled.
                  </p>
                </>
              )}
            </>
          )}

          {displayMode === 'intermission' && (
            <>
              <span className="bingo-display-mode-eyebrow">INTERMISSION</span>
              <h1 className="bingo-display-mode-title">{intermissionMessage}</h1>
              <p className="bingo-display-mode-message">Bingo will continue shortly.</p>
            </>
          )}

          {displayMode === 'announcement' && (
            <>
              <span className="bingo-display-mode-eyebrow">ANNOUNCEMENT</span>
              {announcementTitle && (
                <h1 className="bingo-display-mode-title">{announcementTitle}</h1>
              )}
              {announcementMessage && (
                <p className="bingo-display-mode-message">{announcementMessage}</p>
              )}
              {!announcementTitle && !announcementMessage && (
                <h1 className="bingo-display-mode-title">Please stay tuned.</h1>
              )}
            </>
          )}

          {displayMode === 'standby' && (
            <>
              <span className="bingo-display-mode-eyebrow">BUZZBOARD FLASH</span>
              <h1 className="bingo-display-mode-title">Standby</h1>
              <p className="bingo-display-mode-message">Waiting for the next game</p>
            </>
          )}

          <div className="bingo-display-mode-brand">BUZZBOARD FLA⚡H</div>
        </main>
      </div>
    );
  }

  return (
    <div
      className={`bingo-display-screen theme-${theme}`}
    >
      <div className="bingo-display-top">

        <div className="bingo-display-event">
          <h1>
            {session.eventName ||
              'Bingo'}
          </h1>

          <div className="bingo-display-game-info">
            Game{' '}
            {session.gameNumber ||
              1}

            {session.prizeAmount && (
              <>
                {' • '}
                Prize: $
                {
                  session.prizeAmount
                }
              </>
            )}
          </div>
        </div>

        <div className="bingo-display-ball-statuses">
          <div className="bingo-display-current">

            <div className="bingo-display-current-label">
              Last Called
            </div>

            <div className="bingo-display-current-ball">
              <BingoBall ball={session.currentBall} size="large" />
            </div>

          </div>

          <div className="bingo-display-current bingo-display-next">

            <div className="bingo-display-current-label">
              Next Ball Up
            </div>

            <div className="bingo-display-current-ball">
              <BingoBall ball={session.nextBall} size="large" />
            </div>

            <AudienceAutoCallCountdown
              enabled={Boolean(session.autoCallEnabled) && !session.manualMode}
              nextCallAt={session.autoCallNextAt}
            />

          </div>

          <div className="bingo-display-recent">
            <div className="bingo-display-current-label">
              Recent Calls
            </div>

            {recentCalledBalls.length > 0 ? (
              <div className="bingo-display-recent-list">
                {recentCalledBalls.map((ball, index) => {
                  const normalized = normalizeBingoBall(ball);

                  return (
                    <BingoBall
                      key={`display-recent-${normalized?.number ?? index}`}
                      ball={ball}
                      size={index === 0 ? 'medium' : 'small'}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="bingo-display-recent-empty">No calls yet</div>
            )}
          </div>
        </div>

      </div>

      <div className="bingo-display-main">

        <div className="bingo-display-board">

          {BINGO_LETTERS.map(
            (
              letter,
              rowIndex
            ) => (
              <div
                className="bingo-display-row"
                key={letter}
              >

                <div
                  className={`bingo-display-letter letter-${letter}`}
                >
                  {letter}
                </div>

                {Array.from({
                  length: 15,
                }).map(
                  (
                    _,
                    colIndex
                  ) => {
                    const number =
                      rowIndex *
                        15 +
                      colIndex +
                      1;

                    const called =
                      calledNumbers.has(
                        number
                      );

                    return (
                      <div
                        key={
                          number
                        }
                        className={[
                          'bingo-display-number',
                          called
                            ? 'called'
                            : '',
                        ]
                          .filter(
                            Boolean
                          )
                          .join(
                            ' '
                          )}
                      >
                        {number}
                      </div>
                    );
                  }
                )}

              </div>
            )
          )}

        </div>

        <div className="bingo-display-side">

          <div className="bingo-display-pattern-panel">

            <h2>
              Winning Pattern
            </h2>

            <div className="bingo-display-pattern-name">
              {session.selectedPattern
                ? getPatternLabel(session.selectedPattern)
                : 'None'}
            </div>

            <PatternPreview pattern={activePattern} />

          </div>

          <div className="bingo-display-called-count">
            <span>
              Called
            </span>

            <strong>
              {
                calledNumbers.size
              }
            </strong>
          </div>

        </div>

      </div>

      {calledBallOverlay && (
        <div className="bingo-display-ball-overlay">

          <div
            className={`bingo-display-ball-badge letter-${calledBallOverlay.letter}`}
          >
            <div className="bingo-display-ball-caption">
              Now Calling
            </div>

            <div className="bingo-display-ball-value">
              <span>
                {
                  calledBallOverlay.letter
                }
              </span>

              <strong>
                {
                  calledBallOverlay.number
                }
              </strong>
            </div>
          </div>

        </div>
      )}

      {showWinnerOverlay && (
        <div className="bingo-display-winner-overlay">
          <div className="bingo-display-winner-text">
            BINGO!
          </div>
        </div>
      )}

    </div>
  );
}

export default BingoDisplay;
